import { Link } from "react-router";
import { ellipsis } from "polished";
import React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getElementsById } from "core-integration/src/store/elements/reducers";
import { IElementsById } from "core-integration/src/store/elements/types";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { fetchEntrySummary } from "core-integration/src/store/entries/thunks";
import { IEntry } from "core-integration/src/store/entries/types";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { IEvent } from "core-integration/src/store/events/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { IPlayer } from "core-integration/src/store/player/types";
import { ControlArrowRight } from "../icons/Arrows";

const StyledEventInfo = styled.div`
  margin: ${({ theme }) => theme.space[4]} 0;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    margin: 4rem 0;
  }
`;

const EventScores = styled.ul`
  display: flex;
  margin-bottom: ${({ theme }) => theme.space[4]};
  padding: 0;
  gap: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    justify-content: space-around;
  }
`;

const EventScore = styled.li`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space[3]};
  gap: ${({ theme }) => theme.space[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.primary};
  list-style-type: none;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 1 0 30%;
    margin: 0 1.66%;
  }
`;

const ItemHeading = styled.h4`
  margin: 0;
  color: inherit;
`;

const ItemValue = styled.div`
  ${ellipsis()};
  color: inherit;
  font-size: ${({ theme }) => theme.fontSizes[4]};
  font-weight: bold;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    font-size: 2.5rem;
  }
`;

const EventStats = styled.ul`
  padding: 0;
  list-style-type: none;
  color: ${({ theme }) => theme.colors.black};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
    justify-content: space-around;
    gap: ${({ theme }) => theme.space[2]};
  }
`;

const EventStat = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.space[2]};
  padding: ${({ theme }) => theme.space[3]};
  gap: ${({ theme }) => theme.space[2]};
  border-radius: ${({ theme }) => theme.radii[2]};
  background-color: ${({ theme }) => theme.colors.white};
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    flex: 1 0 30%;
    margin: 0 1.66%;
  }
`;

const ArrowLink = styled(Link)`
  color: white;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

interface IPropsFromState {
  elementsById: IElementsById;
  entry: IEntry | null;
  now: IEvent | null;
  player: IPlayer;
}

interface IPropsFromDispatch {
  fetchEntrySummary: (entryId: number) => void;
}

type Props = IPropsFromState & IPropsFromDispatch;

const EventInfo: React.FC<Props> = ({ elementsById, entry, now, player }) => {
  const { t } = useTranslation();

  if (!now || !entry) {
    return null;
  }
  const matches = now.chip_plays.filter((cp) => cp.chip_name === "wildcard");
  const wildcards = matches.length ? matches[0].num_played : "-";
  return (
    <StyledEventInfo>
      <EventScores>
        <EventScore>
          <ItemHeading>
            {t("eventInfo.averageScore", "Average score")}
          </ItemHeading>
          <ItemValue>{now.average_entry_score || "-"}</ItemValue>
        </EventScore>
        {entry.started_event <= now.id && (
          <EventScore>
            <ItemHeading>{t("eventInfo.yourScore", "Your score")}</ItemHeading>
            <ItemValue>
              <ArrowLink to={`/entry/${player.entry}/event/${now.id}`}>
                {entry.summary_event_points}
                <ControlArrowRight />
              </ArrowLink>
            </ItemValue>
          </EventScore>
        )}
        <EventScore>
          <ItemHeading>
            {t("eventInfo.highestScore", "Highest score")}
          </ItemHeading>
          <ItemValue>
            {now.highest_scoring_entry ? (
              <ArrowLink
                to={`/entry/${now.highest_scoring_entry}/event/${now.id}/`}
              >
                {now.highest_score}
                <ControlArrowRight />
              </ArrowLink>
            ) : (
              "-"
            )}
          </ItemValue>
        </EventScore>
      </EventScores>
      <EventStats>
        {now.id > 1 && (
          <EventStat>
            <ItemHeading>
              {t("eventInfo.transfersMade", "Transfers Made")}
            </ItemHeading>
            <ItemValue>
              {now.transfers_made ? now.transfers_made.toLocaleString() : "-"}
            </ItemValue>
          </EventStat>
        )}
        <EventStat>
          <ItemHeading>
            {t("eventInfo.wildcardsPlayed", "Wildcards Played")}
          </ItemHeading>
          <ItemValue>
            {wildcards === null ? "-" : wildcards.toLocaleString()}
          </ItemValue>
        </EventStat>
        <EventStat>
          <ItemHeading>
            {t("eventInfo.mostCaptained", "Most Captained")}
          </ItemHeading>
          <ItemValue>
            {now.most_captained
              ? elementsById[now.most_captained].web_name
              : "-"}
          </ItemValue>
        </EventStat>
      </EventStats>
    </StyledEventInfo>
  );
};

const mapStateToProps = (state: RootState): IPropsFromState => {
  const player = getPlayerData(state) as IPlayer;
  const entryId = player.entry || 0;
  const now = getCurrentEvent(state) as IEvent;
  return {
    elementsById: getElementsById(state),
    entry: getEntry(state, entryId),
    now,
    player,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch): IPropsFromDispatch => ({
  fetchEntrySummary: (entryId) => dispatch(fetchEntrySummary(entryId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EventInfo);
